import React from 'react';
import Head from 'next/head';
import NavBar from '../components/navbar';
import Layout from '../components/layout';
import config from '../lib/config';
import Image from 'next/image';

import { GetStaticProps } from 'next';
import { fetchAPI, getPrimaryMenuItems } from '../lib/api';
import { ISponsor } from '../models/sponsor';
import { IMenuItem } from '../models/menu-item';
import { IYoastMeta } from '../models/post';
import { IFeaturedImage } from '../models/featured-image';
import PageHeader from '@/components/zakelijk/PageHeader';
import SponsorGrid from '@/components/zakelijk/SponsorGrid';

interface IProps {
	sponsors: ISponsor[];
	primaryMenuItems: IMenuItem[];
	page: {
		title: string;
		content: string | TrustedHTML;
		featuredImage: IFeaturedImage | null;
		seo: IYoastMeta;
	};
}

export default function PartnersPage({ page, primaryMenuItems, sponsors }: IProps) {
	const pageTitle = `${page.title} | ${config.siteName}`;
	const meta = page.seo?.description || page.title;

	return (
		<Layout preview={false} sponsors={sponsors} metaTitle={pageTitle} metaDesc={meta}>
			<Head>
				<title>{pageTitle}</title>
			</Head>
			<NavBar items={primaryMenuItems} />
			<PageHeader
				title={page.title}
				description={
					'Samen met onze partners maken we de Keuken Kampioen Divisie tot de spannendste competitie van Nederland.'
				}
				// badge={displayData.acfZakelijkHeader?.contentBadge}
			/>

			<main className="container mx-auto px-4 py-12 max-w-7xl">
				<SponsorGrid sponsors={sponsors} />
			</main>
		</Layout>
	);
}

export const getStaticProps: GetStaticProps<IProps> = async () => {
	try {
		// Fetch page data by database ID 10045, sponsors with images, and primaryMenuItems
		const [data, primaryMenuItems] = await Promise.all([
			fetchAPI<{ page: any; sponsors: any }>(
				`query PartnersPageQuery {
					page(id: 10045, idType: DATABASE_ID) {
						title
						content
						featuredImage {
							node {
								altText
								sourceUrl
							}
						}
						seo {
							title
							metaDesc
						}
					}
					sponsors(first: 100, where: { orderby: { field: MENU_ORDER, order: ASC } }) {
						nodes {
							title
							featuredImage {
								node {
									sourceUrl
									altText
								}
							}
							sponsor_fields {
								subtitle
								websiteUrl
								mainSponsor
								invertedImage {
									sourceUrl
								}
							}
						}
					}
				}`,
			),
			getPrimaryMenuItems(),
		]);

		const { sponsors, page } = data;

		return {
			props: {
				page: page || { title: 'Partners', content: '', featuredImage: null, seo: { description: '' } },
				sponsors: sponsors.nodes || [],
				primaryMenuItems: primaryMenuItems || [],
			},
			revalidate: 3600, // ISR: Revalidate every 3600 seconds (1 hour)
		};
	} catch (error) {
		console.error('Error fetching partners page data:', error);
		return {
			props: {
				page: { title: 'Partners', content: '', featuredImage: null, seo: { description: '' } },
				sponsors: [],
				primaryMenuItems: [],
			},
			revalidate: 3600,
		};
	}
};
